import { logs, SeverityNumber } from '@opentelemetry/api-logs';
import { LoggerProvider, BatchLogRecordProcessor } from '@opentelemetry/sdk-logs';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { trace } from '@opentelemetry/api';
import { ApiError } from '@api/errors';
import type { LogContext, Logger } from './logger';
import { sanitize, truncateBody } from './sanitize';

export interface OtelLoggerConfig {
  endpoint: string;
  authToken: string;
  serviceName: string;
  serviceVersion?: string;
  environment?: string;
  verbose?: boolean;
}

function normalizeError(error: unknown): LogContext {
  if (error === undefined || error === null) return {};
  if (error instanceof ApiError) {
    return { errorName: 'ApiError', apiStatus: error.status, apiBody: truncateBody(error.body) };
  }
  if (error instanceof Error) return { errorName: error.name, errorMessage: error.message };
  return { errorValue: sanitize(error) };
}

function getTraceContext(): LogContext {
  const span = trace.getActiveSpan();
  if (!span) return {};
  const { traceId, spanId } = span.spanContext();
  return { trace_id: traceId, span_id: spanId };
}

function build(otelLogger: ReturnType<typeof logs.getLogger>, parentContext: LogContext, verbose: boolean): Logger {
  const emit = (severity: SeverityNumber, message: string, context?: LogContext, error?: unknown) => {
    const merged = sanitize({
      ...getTraceContext(),
      ...parentContext,
      ...(context ?? {}),
      ...normalizeError(error),
    }) as LogContext;
    otelLogger.emit({
      severityNumber: severity,
      severityText: SeverityNumber[severity],
      body: message,
      attributes: merged as Record<string, string | number | boolean>,
    });
  };

  const noop = () => {};
  return {
    debug: verbose ? (msg, ctx) => emit(SeverityNumber.DEBUG, msg, ctx) : noop,
    info: verbose ? (msg, ctx) => emit(SeverityNumber.INFO, msg, ctx) : noop,
    warn: (msg, ctx, err) => emit(SeverityNumber.WARN, msg, ctx, err),
    error: (msg, ctx, err) => emit(SeverityNumber.ERROR, msg, ctx, err),
    withContext: (ctx) => build(otelLogger, { ...parentContext, ...ctx }, verbose),
  };
}

export function createOtelLogger(config: OtelLoggerConfig): Logger {
  const resourceAttributes: Record<string, string> = { 'service.name': config.serviceName };
  if (config.serviceVersion) resourceAttributes['service.version'] = config.serviceVersion;
  if (config.environment) resourceAttributes['deployment.environment.name'] = config.environment;

  const provider = new LoggerProvider({
    resource: resourceFromAttributes(resourceAttributes),
    processors: [
      new BatchLogRecordProcessor(
        new OTLPLogExporter({
          url: `${config.endpoint}/v1/logs`,
          headers: { Authorization: `Bearer ${config.authToken}` },
        }),
      ),
    ],
  });
  logs.setGlobalLoggerProvider(provider);

  const otelLogger = logs.getLogger(config.serviceName);
  return build(otelLogger, {}, config.verbose ?? false);
}
